"use client";

import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Heading3, Paragraph } from "../typography";
import { AustraliaMapMaskedCard } from "../australia-map-masked-card";

export const TheChallengeSolution = () => {
  useSectionNavTheme({
    trigger: "#planr-challenge-solution",
    isDark: true,
  });

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
      // Set initial opacity for cards 2 and 3, and solution content
      gsap.set([".australia-card-2", ".australia-card-3"], {
        opacity: 0,
      });

      // Initial reveal animation
      gsap.from(".challenge-title", {
        opacity: 0,
        y: 40,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: "#planr-challenge-solution",
          start: "top 80%",
        },
      });

      // Pinned scroll animation
      const pinnedTl = gsap.timeline({
        ease: "none",
        scrollTrigger: {
          trigger: "#planr-challenge-solution",
          start: "top top",
          end: "+=100%", // Extended scroll distance for all animations
          pin: ".map-cards",
          scrub: 1,
          invalidateOnRefresh: true,
          // markers: true,
        },
      });

      // Reveal Australia map cards in sequence
      pinnedTl
        .to(".australia-card-1", {
          opacity: 0,
        })
        .to(
          ".australia-card-1 .map-card__content",
          {
            y: -300,
          },
          "<"
        )
        .to(
          ".australia-card-2",
          {
            opacity: 1,
          },
          "<"
        )
        .to(".australia-card-2 .map-card__content", {
          y: -300,
        })
        .to([".australia-card-3"], {
          opacity: 1,
        });
    });
  });

  return (
    <section
      id="planr-challenge-solution"
      className="relative py-20 lg:py-32 xl:py-40"
    >
      <div className="absolute inset-0 dark:bg-gradient-to-l from-haizea-green/50 via-haizea-dark to-slate-700/60"></div>
      <div className="container">
        <div className="flex items-start flex-col lg:flex-row gap-10 lg:gap-20">
          <div className="map-cards relative rounded-2xl border border-slate-200 dark:border-slate-500 w-full lg:w-[600px] lg:max-w-1/2 max-h-[60vh] aspect-square shrink-0 bg-slate-50/10 backdrop-blur-sm">
            <AustraliaMapMaskedCard
              title="7.7M"
              subtitle="Square kilometres coverage required"
              className="australia-card-1 w-full h-full bg-gradient-to-br from-haizea-green via-90% via-haizea-orange  to-haizea-blue"
            />
            <AustraliaMapMaskedCard
              title="Real-time"
              subtitle="Continental processing"
              className="australia-card-2 w-full h-full bg-gradient-to-br from-haizea-green via-70% to-haizea-blue"
            />
            <AustraliaMapMaskedCard
              title="PLANR"
              subtitle="Powered by Terrak.io"
              className="australia-card-3 w-full h-full bg-radial from-haizea-orange via-60% via-haizea-green to-haizea-blue"
            />
          </div>
          <div className="relative w-full">
            <ChallengeContent />
            <SolutionContent />
          </div>
        </div>
      </div>
    </section>
  );
};

const ChallengeContent = () => {
  return (
    <div className="space-y-6 text-lg leading-relaxed dark:text-slate-300 bg-white/5 lg:bg-transparent backdrop-blur-sm lg:backdrop-blur-none p-6 lg:p-0">
      <Heading3 className="challenge-title text-slate-800 dark:text-slate-200">
        The Challenge
      </Heading3>
      <div className="">
        <div className="challenge-content space-y-4 lg:space-y-10">
          <Paragraph>
            The Australian Government&apos;s Department of Climate Change,
            Energy, the Environment and Water faced an unprecedented technical
            challenge: creating a national platform that could support
            <span className="font-semibold text-slate-800 dark:text-slate-200">
              {" "}
              real-time environmental impact calculations
            </span>{" "}
            for Australia&apos;s new Nature Repair Market.
          </Paragraph>

          <Paragraph>
            The Platform for Land and Nature Repair (PLANR) needed to enable
            landholders anywhere across Australia to{" "}
            <span className="font-semibold text-slate-800 dark:text-slate-200">
              instantly assess biodiversity and carbon benefits
            </span>{" "}
            of proposed projects at sub-property scale.
          </Paragraph>

          <Paragraph>
            Traditional environmental assessment methods were fundamentally
            inadequate for this vision. Existing approaches required{" "}
            <span className="font-semibold">
              weeks or months of manual analysis
            </span>
            , relied on static datasets, and couldn&apos;t support the
            interactive, on-demand calculations needed for a user-friendly web
            platform.
          </Paragraph>

          <Paragraph>
            Most critically, the calculations couldn&apos;t be performed on
            individual pixels but required
            <span className="font-semibold text-slate-800 dark:text-slate-200">
              {" "}
              complex analysis of surrounding environmental context
            </span>
            —habitat connectivity, ecosystem interactions, and landscape-scale
            biodiversity patterns.
          </Paragraph>
        </div>
      </div>
    </div>
  );
};

const SolutionContent = () => {
  const solutionFeatures = [
    {
      title: "Real-Time Environmental Analysis",
      description:
        "Terrak.io's cloud-based architecture enables instant calculations across Australia's entire landmass, with advanced algorithms analyzing environmental context at landscape scale.",
    },
    {
      title: "Unique Technical Capabilities",
      description:
        "First-of-its-kind real-time calculation of habitat connectivity changes—a capability not available anywhere else. Multi-scale environmental modeling delivers complex impact assessments within seconds.",
    },
    {
      title: "Platform Integration",
      description:
        "Terrak.io provides the complete backend infrastructure enabling PLANR's user-friendly web interface, hosting all environmental datasets required for national-scale analysis.",
    },
  ];

  return (
    <div className="mt-10 lg:mt-40 space-y-6 text-lg leading-relaxed dark:text-slate-300 bg-white/5 lg:bg-transparent backdrop-blur-sm lg:backdrop-blur-none p-6 lg:p-0">
      <Heading3 className="text-slate-800 dark:text-slate-200">
        The Solution
      </Heading3>
      <div className="">
        <div className="solution-content space-y-4 lg:space-y-10">
          <Paragraph>
            DCCEEW partnered with Haizea Analytics to leverage the Terrak.io
            platform&apos;s unique{" "}
            <span className="font-semibold text-slate-800 dark:text-slate-200">
              real-time analytics capabilities
            </span>
            , creating Australia&apos;s first national environmental markets
            platform with instant impact assessment functionality.
          </Paragraph>

          {solutionFeatures.map((feature, index) => (
            <div key={index} className="space-y-2">
              <h4 className="text-xl font-semibold text-emerald-600 dark:text-emerald-400">
                {feature.title}
              </h4>
              <Paragraph>{feature.description}</Paragraph>
            </div>
          ))}

          <Paragraph>
            The world&apos;s first platform enabling{" "}
            <span className="font-semibold text-slate-800 dark:text-slate-200">
              instant calculation of habitat connectivity changes
            </span>{" "}
            across continental scales—powered by Terrak.io&apos;s advanced
            scripting language and scalable processing infrastructure.
          </Paragraph>
        </div>
      </div>
    </div>
  );
};
